import React, { FC, ReactElement, useRef } from 'react';
import {
  Popover as ChakraPopover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Flex,
  useDisclosure,
  ButtonProps,
  PopoverProps,
  PopoverContentProps,
  useOutsideClick,
  PopoverBodyProps,
  PopoverFooterProps,
} from '@chakra-ui/react';

type ChildProps = {
  onClose?: () => void;
};

type Props = {
  triggerElement: React.ReactNode;
  children: ReactElement<ChildProps>; // Ensure children receive the correct prop
  ctaLabel?: string;
  ctaOnClick?: (args) => void;
  secondaryCtaLabel?: string;
  secondaryCtaProps?: ButtonProps;
  ctaProps?: ButtonProps;
  secondaryCtaOnClick?: (args) => void;
  showArrow?: boolean;
  showCloseButton?: boolean;
  popoverProps?: PopoverProps;
  popoverBodyProps?: PopoverBodyProps;
  popoverContentProps?: PopoverContentProps;
  popoverActions?: any;
  popoverFooterProps?: PopoverFooterProps;
  footer?: React.ReactNode | React.ComponentType<any>;
};

export const KPopover: FC<Props> = ({
  triggerElement,
  children,
  ctaLabel,
  ctaOnClick,
  secondaryCtaLabel,
  ctaProps,
  secondaryCtaProps,
  secondaryCtaOnClick,
  showArrow = true,
  showCloseButton = true,
  footer = null,
  popoverProps = {},
  popoverContentProps = {},
  popoverBodyProps = {},
  popoverActions = {},
  popoverFooterProps = {},
}) => {
  const popoverDisclosure = useDisclosure();
  const { isOpen, onOpen, onClose } = popoverActions;
  const popoverRef = useRef(null);
  const childWithProps = React.isValidElement(children)
    ? React.cloneElement(children, { onClose })
    : children;

  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });

  return (
    <ChakraPopover
      initialFocusRef={null}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      autoFocus={false}
      closeOnBlur={true}
      {...popoverProps}
    >
      {/*@ts-ignore */}
      <PopoverTrigger>{triggerElement}</PopoverTrigger>
      <PopoverContent ref={popoverRef} {...popoverContentProps}>
        {showArrow && <PopoverArrow />}
        {showCloseButton && <PopoverCloseButton />}

        <PopoverBody {...popoverBodyProps}>{childWithProps}</PopoverBody>

        {React.isValidElement(footer) ? (
          footer
        ) : (
          <>
            {(ctaLabel || secondaryCtaLabel) && (
              <PopoverFooter {...popoverFooterProps}>
                <Flex gap="10px" w="100%" justifyContent="end">
                  {secondaryCtaLabel && (
                    <Button
                      variant="secondary"
                      onClick={(props) => {
                        if (secondaryCtaOnClick) {
                          secondaryCtaOnClick({ popoverDisclosure, ...props });
                        } else onClose();
                      }}
                      {...secondaryCtaProps}
                    >
                      {secondaryCtaLabel}
                    </Button>
                  )}
                  {ctaLabel && (
                    <Button
                      variant="primary"
                      onClick={(props) =>
                        ctaOnClick({ popoverDisclosure, ...props })
                      }
                      {...ctaProps}
                    >
                      {ctaLabel}
                    </Button>
                  )}
                </Flex>
              </PopoverFooter>
            )}
          </>
        )}
      </PopoverContent>
    </ChakraPopover>
  );
};
